import { ChangeEvent, useEffect, useRef, useState } from 'react'

import styles from './Formselect.module.css'

type OptionType =
  | {
      value: string
      label: string
    }
  | string

export type FormselectProps = {
  variant?: 'select' | 'multi' | 'enquiry' | 'state' | 'phone' | 'valueLabel'
  options?: OptionType[] | { label: string; values: string[] }[]
  multiOptions?: string[]
  name: string
  label?: string
  isRequired?: boolean
  showRequired?: boolean
  phoneEnquiryOptions?: any
  onChange?: (value: string) => void
  value?: string
}

export const PhnxFormselect: React.FC<FormselectProps> = ({
  variant = 'select',
  options,
  multiOptions,
  name,
  label,
  isRequired = false,
  showRequired = true,
  phoneEnquiryOptions,
  onChange,
  value,
}) => {
  const [selectedState, setSelectedState] = useState('')
  const [selectedEnquiryType, setSelectedEnquiryType] = useState('')
  const [selectedEnquiryType2, setSelectedEnquiryType2] = useState('')
  const [selectedValue, setSelectedValue] = useState('')
  const [valueValue, setValueValue] = useState('')
  const [multiSelected, setMultiSelected] = useState('')

  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
  const [dropdownWidth, setDropdownWidth] = useState<number>(0)
  const selectRef = useRef<HTMLSelectElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (selectRef.current && Array.isArray(options)) {
      let maxWidthOption = 0
      let padding = 16
      if (variant === 'multi') {
        options.forEach((group) => {
          if (typeof group === 'object' && 'values' in group && Array.isArray(group.values)) {
            group.values.forEach((option) => {
              const optionWidth = measureTextWidth(option, selectRef.current!)
              maxWidthOption = Math.max(maxWidthOption, optionWidth)
            })
          }
        })
      } else if (variant === 'select') {
        options.forEach((item) => {
          if (typeof item === 'string') {
            const optionWidth = measureTextWidth(item, selectRef.current!)
            maxWidthOption = Math.max(maxWidthOption, optionWidth)
          }
        })

        padding = 32
      }

      const tickWidth = 24
      setDropdownWidth(maxWidthOption + padding + tickWidth)
    }
  }, [options, variant])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const measureTextWidth = (text: string, element: HTMLElement) => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (context) {
      const style = window.getComputedStyle(element)
      context.font = style.font
      return context.measureText(text).width
    }
    return 0
  }

  const handleOptionClick = (option: string) => {
    setSelectedOptions((prevSelectedOptions) =>
      prevSelectedOptions.includes(option)
        ? prevSelectedOptions.filter((o) => o !== option)
        : [...prevSelectedOptions, option]
    )
    setIsDropdownOpen(false)
  }

  const handleDeselect = (optionToDeselect: string) => {
    setSelectedOptions((prevSelectedOptions) =>
      prevSelectedOptions.filter((option) => option !== optionToDeselect)
    )
  }

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = event.target.value
    setMultiSelected(selectedOption)
    if (selectedOption) {
      handleOptionClick(selectedOption)
    }
  }

  const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedState(event.target.value)
  }

  const stateContent = (
    <div className={styles['formselect-state']}>
      {label && <label htmlFor={name}>State {isRequired && showRequired && '*'}</label>}
      <select
        id={name}
        name={name}
        required={isRequired}
        className={styles['form-select']}
        onChange={handleStateChange}
        value={selectedState}
      >
        <option value="">Select a state</option>
        <option value="NSW">New South Wales</option>
        <option value="VIC">Victoria</option>
        <option value="QLD">Queensland</option>
        <option value="SA">South Australia</option>
        <option value="WA">Western Australia</option>
        <option value="TAS">Tasmania</option>
        <option value="NT">Northern Territory</option>
        <option value="ACT">Australian Capital Territory</option>
      </select>
    </div>
  )

  const enquiryContent = () => {
    const handleEnquiryTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedEnquiryType(event.target.value)
    }
    const handleEnquiryTypeChange2 = (event: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedEnquiryType2(event.target.value)
    }

    return (
      <>
        <div className={styles['formselect-enquiry']}>
          {label && <label htmlFor={name}>Enquiry Type {isRequired && showRequired && '*'}</label>}
          <select
            id={name}
            name={name}
            required={isRequired}
            className={styles['form-select']}
            onChange={handleEnquiryTypeChange}
            value={selectedEnquiryType}
          >
            <option value="">Select an enquiry type</option>
            <option value="Services/Business Enquiry">Services/Business Enquiry</option>
            <option value="Careers/Job Enquiry">Careers/Job Enquiry</option>
            <option value="Media Enquiry">Media Enquiry</option>
            <option value="Vendor Enquiry">Vendor Enquiry</option>
            <option value="Other">Other</option>
          </select>
        </div>
        {selectedEnquiryType === 'Services/Business Enquiry' && (
          <div className={styles['formselect-enquiry']}>
            {label && <label htmlFor="serviceType">Service type</label>}
            <select
              id="serviceType"
              name="serviceType"
              required={selectedEnquiryType === 'Services/Business Enquiry' ? isRequired : false}
              className={styles['form-select']}
              onChange={handleEnquiryTypeChange2}
              value={selectedEnquiryType2}
            >
              <option value="">Select a service</option>
              <option value="Accounting and Business Advisory">
                Accounting and Business Advisory
              </option>
              <option value="External Audit">External Audit</option>
              <option value="Corporate Finance">Corporate Finance</option>
              <option value="Consulting">Consulting</option>
              <option value="General Insurance">General Insurance</option>
              <option value="Lending and Finance">Lending and Finance</option>
              <option value="Risk Insurance">Risk Insurance</option>
              <option value="SMSF Administration and Advisory">
                SMSF Administration and Advisory
              </option>
              <option value="Tax Advisory">Tax Advisory</option>
              <option value="Wealth Management">Wealth Management</option>
            </select>
          </div>
        )}
      </>
    )
  }

  const selectContent = (
    <div className={styles['formselect-select']}>
      {label && (
        <label htmlFor={name}>
          {label} {isRequired && showRequired && '*'}
        </label>
      )}
      <select
        id={name}
        name={name}
        required={isRequired}
        className={styles['form-select']}
        style={{ minWidth: `${dropdownWidth}px` }}
        ref={selectRef}
        onChange={(e) => {
          if (onChange) {
            onChange(e.target.value)
          }
          setSelectedValue(e.target.value)
        }}
        value={selectedValue}
      >
        <option value="">Please Select</option>
        {Array.isArray(options) &&
          options.map((option, index) => {
            if (typeof option === 'object' && 'value' in option) {
              return (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              )
            }
            // Handle string options for backward compatibility
            if (typeof option === 'string') {
              return (
                <option key={index} value={option}>
                  {option}
                </option>
              )
            }
            return null
          })}
      </select>
    </div>
  )

  const multiContent = (
    <div className={styles['formselect-select']}>
      {label && (
        <label htmlFor={name}>
          {label} {isRequired && showRequired && '*'}
        </label>
      )}
      <div className={`container ${styles['multi']}`}>
        <div className="relative">
          <select
            id={`${name}-multi`}
            style={{ minWidth: `${dropdownWidth}px` }}
            onChange={handleSelectChange}
            ref={selectRef}
            required={selectedOptions.length === 0 && isRequired}
            value={multiSelected}
          >
            <option value="" disabled>
              Select options
            </option>
            {Array.isArray(multiOptions) &&
              multiOptions.map((option, index) => {
                return (
                  <option key={`${index}`} value={option}>
                    {option}
                    {selectedOptions.includes(option) && <span>&#10003;</span>}
                  </option>
                )
              })}
          </select>
        </div>

        <input
          type="hidden"
          id={name}
          name={name}
          value={selectedOptions.join(',')}
          data-type="multi"
        />
        {selectedOptions.length > 0 && (
          <div className={styles['multi-options']}>
            {selectedOptions.map((option, index) => (
              <div key={index} className={styles['multi-selected']}>
                {option}
                <button
                  type="button"
                  className={styles['multi-close-btn']}
                  onClick={() => handleDeselect(option)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  const phoneContent = (
    <div className={styles['formselect-select']}>
      {label && (
        <label htmlFor={name}>
          {label} {isRequired && showRequired && '*'}
        </label>
      )}
      <div className={`container ${styles['multi']}`}>
        <div className="relative">
          <select
            id={`${name}-multi`}
            style={{ minWidth: `${dropdownWidth}px` }}
            value={value ?? ''}
            onChange={handleSelectChange}
            ref={selectRef}
            required={selectedOptions.length === 0 && isRequired}
          >
            <option value="" disabled>
              Select options
            </option>
            {Array.isArray(phoneEnquiryOptions) && phoneEnquiryOptions.length > 0 ? (
              phoneEnquiryOptions.map((optionGroup, index) => {
                const groupLabel = Object.keys(optionGroup)[0]
                const groupOptions = optionGroup[groupLabel]

                if (!groupOptions) {
                  console.error('groupOptions is undefined for groupLabel:', groupLabel)
                  return null
                }

                return (
                  <optgroup key={index} label={groupLabel}>
                    {groupOptions.map((value: any, idx: number) => (
                      <option key={`${index}-${idx}`} value={`${groupLabel}: ${value}`}>
                        {value}
                        {selectedOptions.includes(`${groupLabel}: ${value}`) && (
                          <span>&#10003;</span>
                        )}
                      </option>
                    ))}
                  </optgroup>
                )
              })
            ) : (
              <option value="" disabled>
                No options available
              </option>
            )}
          </select>
        </div>

        <input
          type="hidden"
          id={name}
          name={name}
          value={selectedOptions.join(',')}
          data-type="multi"
        />
        {selectedOptions.length > 0 && (
          <div className={styles['multi-options']}>
            {selectedOptions.map((option, index) => (
              <div key={index} className={styles['multi-selected']}>
                {option}
                <button
                  type="button"
                  className={styles['multi-close-btn']}
                  style={{
                    backgroundColor: 'transparent',
                    color: 'black',
                    border: 'none',
                  }}
                  onClick={() => handleDeselect(option)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  const valueLabelContent = (
    <div className={styles['formselect-select']}>
      {label && (
        <label htmlFor={name}>
          {label} {isRequired && showRequired && '*'}
        </label>
      )}
      <select
        id={name}
        name={name}
        required={isRequired}
        className={styles['form-select']}
        style={{ minWidth: `${dropdownWidth}px` }}
        ref={selectRef}
        onChange={(e) => {
          if (onChange) {
            onChange(e.target.value)
          }
          setValueValue(e.target.value)
        }}
        value={valueValue}
      >
        <option value="">Please Select</option>
        {Array.isArray(options) &&
          options.map((option, index) => {
            if (typeof option === 'object' && 'value' in option) {
              return (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              )
            }
            // Handle string options for backward compatibility
            if (typeof option === 'string') {
              return (
                <option key={index} value={option}>
                  {option}
                </option>
              )
            }
            return null
          })}
      </select>
    </div>
  )

  return (
    <div className={styles['Phnxformselect']}>
      {variant === 'state'
        ? stateContent
        : variant === 'enquiry'
          ? enquiryContent()
          : variant === 'multi'
            ? multiContent
            : variant === 'phone'
              ? phoneContent
              : variant === 'valueLabel'
                ? valueLabelContent
                : selectContent}
    </div>
  )
}
